import React, { useState, useMemo } from 'react';
import NameInput from '../Inputs/NameInput';
import PhoneInput from '../Inputs/PhoneInput';
import AditionalPhoneInput from '../Inputs/AditionalPhoneInput';
import CEPInput from '../Inputs/CEPInput';
import AddressInput from '../Inputs/AddressInput';
import NumberInput from '../Inputs/NumberInput';
import ComplementInput from '../Inputs/Complement';
import DistrictInput from '../Inputs/DistrictInput';
import StateInput from '../Inputs/StateInput';
import CityInput from '../Inputs/CityInput';
import HeaderSection from '../HeaderSection';
import { LineFieldsContainer, PersonalDataContainer } from './styles';
import { ButtonPrimary, Spinner } from '../StyledComponent';
import { validarCep, validarTelefone } from '../../Utils/validations';

const PersonalData = ({
  tema,
  userDataCopy,
  setUserDataCopy,
  isUserDataEqualsUserDataCopy,
  haveAnyFieldsEditing,
  toggleEdit,
  handleCancel,
  onClickSave,
  isErrorApiCep,
  setIsErrorApiCep,
  userDataValidation,
  setUserDataValidation,
}) => {
  const [isEditable, setIsEditable] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  async function handleSave() {
    onClickSave(setIsEditable, setIsLoading);
  }

  const isAbleToSave = useMemo(() => {
    const { Celular, Telefone, Enderecos } = userDataCopy;
    const { Cep, Logradouro, Numero, Bairro, Estado, Cidade, Complemento } =
      Enderecos?.length > 0 && Enderecos[0];

    let ableToSave = true;

    const fields = {
      Celular,
      Cep,
      Logradouro,
      Numero,
      Bairro,
      Estado,
      Cidade,
      Complemento,
      Telefone,
    };

    const fieldsEntries = Object.entries(fields);

    for (const [key, value] of fieldsEntries) {
      const valid = validateData(key, value);
      if (!valid) {
        ableToSave = false;
        break;
      }
    }

    return ableToSave;
  }, [userDataCopy]);

  function validateData(key, value) {
    switch (key) {
      case 'Celular':
        return validarTelefone(value);
      case 'Cep':
        return validarCep(value);
      case 'Telefone':
        //se tiver telefone preenchido, valida
        if (value) {
          return validarTelefone(value);
        }
        return true;

      case 'Complemento':
        //se tiver complemento preenchido, valida
        if (value) {
          return !!value?.trim();
        }
        return true;

      default:
        return !!value?.trim();
    }
  }

  function validate(e) {
    const { id, value } = e.target;
    setUserDataValidation({
      ...userDataValidation,
      [id]: validateData(id, value),
    });
  }

  const disableSaveButton =
    isUserDataEqualsUserDataCopy || !isAbleToSave || isLoading;

  const disableFields = !isEditable || isLoading;

  const disableFieldsFilledByApiViaCep = !isErrorApiCep || isLoading;

  const hasAddress = userDataCopy?.Enderecos?.length > 0;

  return (
    <PersonalDataContainer>
      <HeaderSection
        title={'Dados pessoais'}
        subtitle={
          'São as suas informações pessoais, é importante que estejam corretas e atualizadas:'
        }
        tema={tema}
        onEdit={toggleEdit}
        onCancel={handleCancel}
        isEditable={isEditable}
        setIsEditable={setIsEditable}
        marginTop={120}
        mobileMarginTop={80}
        haveAnyFieldsEditing={haveAnyFieldsEditing}
        isLoading={isLoading}
      />

      <div className='container-fields-personal-data'>
        <LineFieldsContainer>
          <NameInput width={560} disabled value={userDataCopy.Nome} />
          <PhoneInput
            width={250}
            disabled={disableFields}
            value={userDataCopy?.Celular}
            setValue={setUserDataCopy}
            userData={userDataCopy}
            validate={validate}
            valid={userDataValidation?.Celular}
          />
          <AditionalPhoneInput
            width={250}
            disabled={disableFields}
            value={userDataCopy?.Telefone}
            setValue={setUserDataCopy}
            userData={userDataCopy}
            validate={validate}
            valid={userDataValidation?.Telefone}
          />
        </LineFieldsContainer>
        <LineFieldsContainer>
          <CEPInput
            width={353}
            disabled={disableFields}
            value={hasAddress && userDataCopy?.Enderecos[0].Cep}
            setValue={setUserDataCopy}
            userData={userDataCopy}
            setIsErrorApiCep={setIsErrorApiCep}
            isErrorApiCep={isErrorApiCep}
            validate={validate}
            valid={userDataValidation?.Cep}
          />
          <AddressInput
            width={723}
            disabled={disableFieldsFilledByApiViaCep}
            value={hasAddress && userDataCopy?.Enderecos[0].Logradouro}
            setValue={setUserDataCopy}
            userData={userDataCopy}
            validate={validate}
            valid={userDataValidation?.Logradouro}
            isErrorApiCep={isErrorApiCep}
          />
        </LineFieldsContainer>
        <LineFieldsContainer>
          <NumberInput
            width={353}
            disabled={disableFields}
            value={hasAddress && userDataCopy?.Enderecos[0].Numero}
            setValue={setUserDataCopy}
            userData={userDataCopy}
            validate={validate}
            valid={userDataValidation?.Numero}
          />
          <ComplementInput
            width={353}
            disabled={disableFields}
            value={hasAddress && userDataCopy?.Enderecos[0].Complemento}
            setValue={setUserDataCopy}
            userData={userDataCopy}
            validate={validate}
            valid={userDataValidation?.Complemento}
          />
          <DistrictInput
            width={353}
            disabled={disableFieldsFilledByApiViaCep}
            value={
              userDataCopy?.Enderecos?.length > 0 &&
              userDataCopy?.Enderecos[0].Bairro
            }
            setValue={setUserDataCopy}
            userData={userDataCopy}
            validate={validate}
            valid={userDataValidation?.Bairro}
            isErrorApiCep={isErrorApiCep}
          />
        </LineFieldsContainer>
        <LineFieldsContainer>
          <StateInput
            width={353}
            disabled={disableFieldsFilledByApiViaCep}
            value={hasAddress && userDataCopy?.Enderecos[0].Estado}
            setValue={setUserDataCopy}
            userData={userDataCopy}
            validate={validate}
            valid={userDataValidation?.Estado}
            isErrorApiCep={isErrorApiCep}
          />
          <CityInput
            width={723}
            disabled={disableFieldsFilledByApiViaCep}
            value={hasAddress && userDataCopy?.Enderecos[0].Cidade}
            setValue={setUserDataCopy}
            userData={userDataCopy}
            validate={validate}
            valid={userDataValidation?.Cidade}
            isErrorApiCep={isErrorApiCep}
          />
        </LineFieldsContainer>
        {isEditable && (
          <ButtonPrimary
            fullWidth
            disabled={disableSaveButton}
            tema={tema}
            onClick={handleSave}
          >
            SALVAR ALTERAÇÕES
            {isLoading && <Spinner />}
          </ButtonPrimary>
        )}
      </div>
    </PersonalDataContainer>
  );
};
export default PersonalData;
